import {
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  Stack,
  StackDivider,
  Text,
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { POSTER_DEMO_DATA } from '../posterDemoData';
import { POSTER_DEMO_IMAGES, POSTER_DEMO_ICONS } from '../posterDemoAssets';
import { EventGuideMorePopover } from './EventGuideMorePopover';
import type { DemoScreen } from '../ApplePosterDemo';

const ExpandIcon = () => (
  <Icon
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    boxSize="12px"
  >
    <path d="M15 3h6v6" />
    <path d="M9 21H3v-6" />
    <path d="M21 3l-7 7" />
    <path d="M3 21l7-7" />
  </Icon>
);

interface EventGuideScreenProps {
  onBack: () => void;
  onNavigate: (screen: DemoScreen) => void;
}

const { event, venue, weather, urls } = POSTER_DEMO_DATA;

const GUIDE_LINKS: { href: string; icon: string; alt: string }[] = [
  { href: urls.policy, icon: POSTER_DEMO_ICONS.bagPolicy, alt: 'Bag policy' },
  { href: urls.food, icon: POSTER_DEMO_ICONS.food, alt: 'Food' },
  {
    href: urls.transit,
    icon: POSTER_DEMO_ICONS.public,
    alt: 'Public transport',
  },
];

export const EventGuideScreen = ({
  onBack,
  onNavigate,
}: EventGuideScreenProps) => (
  <Box
    position="absolute"
    inset="6% 7.5% 8% 7.5%"
    bg="#F2F2F7"
    borderRadius="28px"
    overflow="hidden"
    display="flex"
    flexDirection="column"
  >
    <Flex
      align="center"
      justify="center"
      position="relative"
      px={2}
      pt={7}
      pb={2}
      flexShrink={0}
    >
      <IconButton
        aria-label="Back"
        icon={<ChevronLeftIcon boxSize="18px" />}
        size="xs"
        variant="ghost"
        borderRadius="full"
        position="absolute"
        left={1}
        onClick={onBack}
      />
      <Text fontSize="12px" fontWeight="600" color="gray.900" noOfLines={1}>
        {event.game}
      </Text>
    </Flex>

    <Box flex={1} overflowY="auto" px={2} pt={4} pb={3}>
      <Flex gap="6px" mb={6} align="stretch">
        {GUIDE_LINKS.map(({ href, icon, alt }) => (
          <Box
            key={alt}
            as="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            flex={1}
            transition="transform 0.15s ease"
            _hover={{ transform: 'scale(1.06)' }}
            _active={{ transform: 'scale(0.98)' }}
          >
            <Image src={icon} alt={alt} w="100%" />
          </Box>
        ))}
        <EventGuideMorePopover />
      </Flex>

      <Flex
        bg="white"
        borderRadius="12px"
        p={3}
        justify="space-between"
        align="center"
        mb={6}
      >
        <Box>
          <Text fontSize="10px" color="gray.500">
            {venue.weatherCity}
          </Text>
          <Text fontSize="20px" fontWeight="600" lineHeight="1">
            {weather.tempHigh}{' '}
            <Text as="span" color="gray.400">
              {weather.tempLow}
            </Text>
          </Text>
        </Box>
        <Text fontSize="10px" color="gray.500">
          {weather.condition}
        </Text>
      </Flex>

      <Text fontSize="11px" color="gray.500" mb={1} px={1}>
        {venue.name}
      </Text>

      <Box
        as="button"
        onClick={() => onNavigate('venueMap')}
        display="block"
        position="relative"
        w="100%"
        borderRadius="12px"
        overflow="hidden"
        mb={6}
      >
        <Image
          src={POSTER_DEMO_IMAGES.venueMap}
          alt="Venue map - tap to enlarge"
          w="100%"
        />
        <Flex
          position="absolute"
          top="6px"
          right="6px"
          align="center"
          justify="center"
          p="3px"
          borderRadius="6px"
          bg="blackAlpha.600"
          color="white"
        >
          <ExpandIcon />
        </Flex>
      </Box>

      <Box bg="white" borderRadius="12px" px={3}>
        <Stack spacing={0} divider={<StackDivider borderColor="gray.100" />}>
          <Flex justify="space-between" py="6px">
            <Text fontSize="11px" color="gray.800">
              Venue Open
            </Text>
            <Text fontSize="11px" color="gray.500">
              {event.gatesOpen}
            </Text>
          </Flex>
          <Flex justify="space-between" py="6px">
            <Text fontSize="11px" color="gray.800">
              Venue Curfew
            </Text>
            <Text fontSize="11px" color="gray.500">
              {event.venueCurfew}
            </Text>
          </Flex>
        </Stack>
      </Box>
    </Box>
  </Box>
);
